/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useCallback } from 'react';
import useAxios from 'axios-hooks';
import { jsx, css } from '@emotion/react';
import { Spinner, Pivot, PivotItem, Text } from '@fluentui/react';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { 
    validationSchema, mapInitialValues, route, basicInfoFields, basePath, removeBaseUrl,
    successMsg, setErrorMsg, rootCss, unsavedMsg, getHeroImgOptions, featureVideoFields
 } from '../Helpers/CopyAndFunctions';
import useDataState from '../../../../Util/useDataState';
import useHandleSave from '../../../../Util/useHandleSave';
import FormStepWrapper from '../../../../Forms/FormStepWrapper';
import BasicInfoStep from '../../../../Forms/Steps/BasicInfoStep';
import Toggle from "../../../../Forms/Toggle";
import AffinityContent from '../Components/AffinityContent';
import AffinityTools from '../Components/AffinityTools';
import AffinityCourses from '../Components/AffinityCourses';
import FeatureFields from '../Components/FeatureFields';
import TextField from '../../../../Forms/TextField';
import FluentSelect from '../../../../Forms/FluentSelect';
import CobrandDropDown from "../../../Checklist/Checklists/Forms/CobrandDropDown";
import AffinityVideos from '../Components/AffinityVideos';
import FeatureSummary from '../../../../Util/FeatureSummary';

const affinityGroupsSummary = [
    {
        selected: "0",
        label: "Basic Info",
        summary: "Set the slug, topic tag, and hero image for your affinity group. The hero image appears at the top of the landing page.",
        imageUrlPath: "affinity-basic-info",
    },
    {
        selected: "1",
        label: "Featured",
        summary: "Configure featured content blocks that appear prominently on the affinity group landing page. These are the same featured blocks found on the logged out page.",
        imageUrlPath: "affinity-featured",
    },
    {
        selected: "2",
        label: "Featured Video",
        summary: "Select a featured video to display on the affinity group page and manage the video list.",
        imageUrlPath: "affinity-featured-video",
    },
    {
        selected: "3",
        label: "Primary Content",
        summary: "Configure the primary content section header and select content items to display. Enable 'See More' to link to the full tag page.",
        imageUrlPath: "affinity-primary-content",
    },
    {
        selected: "4",
        label: "Courses",
        summary: "Set the courses section header and select which courses to feature on this affinity group page.",
        imageUrlPath: "affinity-courses",
    },
    {
        selected: "5",
        label: "Tools",
        summary: "Configure the tools section header and select which tools to display on the affinity group landing page.",
        imageUrlPath: "affinity-tools",
    },
    {
        selected: "6",
        label: "Secondary Content",
        summary: "Configure an additional content section with its own header and content items, separate from the primary content.",
        imageUrlPath: "affinity-secondary-content",
    },
    {
        selected: "7",
        label: "Cobrand On Settings",
        summary: "Select which cobrands should have access to this affinity group. Only selected cobrands will display this landing page.",
        imageUrlPath: "affinity-cobrand-settings",
    },
];

const textCss = css`
    font-family: "Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
    -webkit-font-smoothing: antialiased;
    font-size: 14px;
    font-weight: 600;
    color: rgb(50, 49, 48);
    box-sizing: border-box;
    box-shadow: none;
    margin: 0px;
    display: block;
    padding: 5px 0px;
    overflow-wrap: break-word;
`;

const WizardPage = () => {
    const { affinityGroupSettingId: idFromParams } = useParams();
    const {
        data: affinityGroupData,
        isReady,
        submitForm,
        showSuccess,
        showError,
        setShowError,
        setShowSuccess,
    } = useDataState(idFromParams, route, basePath);

    const [selectedKey, setSelectedKey] = useState('0');
    const handleLinkClick = useCallback((item) => setSelectedKey(String(item.props.itemKey)), []);
    const [{ data: dataOptionsAll, loading: dataOptionsAllLoading }] = useAxios('/resources/content/admin/resource-data');

    if (!isReady || !dataOptionsAll || dataOptionsAllLoading) {
        return <Spinner className="absolute-center" text="Loading Affinity Groups Settings..." />;
    }

    return (
        <Formik
            validateOnMount={true}
            initialValues={mapInitialValues(affinityGroupData)}
            onSubmit={async (values, { resetForm }) => {
                if (!values.settings.affinityGroupSettingId) values.settings.affinityGroupSettingId = 0;
                values.settings.homeHeroImagePath = removeBaseUrl(values.settings.homeHeroImage);
                values.settings.featureBlockImage1Path = removeBaseUrl(values.settings.featureBlockImage1);
                values.settings.featureBlockImage2Path = removeBaseUrl(values.settings.featureBlockImage2);
                values.settings.featureBlockImage3Path = removeBaseUrl(values.settings.featureBlockImage3);
                if (!values.featuredVideo?.contentId) {
                    values.featuredVideo = values.dataOptions.videos.find(v => v.contentId === values.featuredVideo) || null;
                }
                const { dataOptions, tagOrPrimaryContent, tagOrSecondaryContent, ...valuesWithoutDataOptions } = values;
                await submitForm({ data: valuesWithoutDataOptions }, resetForm, 'settings.affinityGroupSettingId');
            }}
            isInitialValid={false}
            validationSchema={validationSchema}
        >
            {({ errors, values, isValid, handleSubmit, setFieldValue }) => {
                const handleSave = useHandleSave(setShowError);
                const imageOptions = values ? getHeroImgOptions(values) : [];
                const summaryObj = affinityGroupsSummary.find(s => String(s.selected) === String(selectedKey));

                console.log('values', values, 'errors', errors);

                if (dataOptionsAll && isReady) {
                    values.dataOptions = dataOptionsAll;
                }

                return values ? (
                    <>
                        <FormStepWrapper
                            handleSave={() => handleSave(isValid, handleSubmit)}
                            showSuccess={showSuccess}
                            showError={showError}
                            successMessage={successMsg}
                            errorMessage={setErrorMsg(showError, errors)}
                            unsavedMsg={unsavedMsg}
                            rootCss={rootCss}
                            showNavigation={false}
                            setShowSuccess={setShowSuccess}
                            setShowError={setShowError}
                        >
                            <div className="step-box">
                                <Pivot selectedKey={String(selectedKey)} onLinkClick={handleLinkClick}>
                                    <PivotItem headerText="Basic Info" itemKey="0">
                                        <div className="max-800">
                                            <TextField
                                                label="Slug"
                                                name="settings.slug"
                                                required
                                            />
                                            <div className="max-800">
                                            <FluentSelect
                                                options={values?.dataOptions?.topics.map(t => ({ value: t.conTagID, label: `${t.conTagID} - ${t.tagName} - ${t.tagSlug}` }))}
                                                label='Group Topic'
                                                name='settings.conTagID'
                                                component={FluentSelect}
                                            />
                                            <div className="preview-upload-help">Search for tags by Title or ID. Then select to add to your affinity group.</div>
                                            </div>
                                            <div css={css`padding-bottom: 30px;`}>
                                                <BasicInfoStep
                                                    fields={basicInfoFields}
                                                    imgUploadOptions={imageOptions}
                                                    showImgUploader
                                                />
                                            </div>
                                        </div>
                                    </PivotItem>
                                    <PivotItem headerText="Featured" itemKey="1">
                                         <div className="max-800">
                                            <FeatureFields />
                                        </div>
                                    </PivotItem>
                                    <PivotItem headerText="Featured Video" itemKey="2">
                                        <div className="max-800">
                                            <BasicInfoStep fields={featureVideoFields} />
                                            <FluentSelect
                                                options={values?.dataOptions?.videos?.map(v => ({ value: v.contentId, label: `${v.contentId} - ${v.title}` })) || []}
                                                label="Featured Video"
                                                name="featuredVideo"
                                                component={FluentSelect}
                                            />
                                            <hr />
                                            <Text css={textCss}>Featured Video List</Text>
                                            <AffinityVideos />
                                        </div>
                                    </PivotItem>
                                    <PivotItem headerText="Primary Content" itemKey="3">
                                        <TextField
                                            label="Primary Content Header"
                                            name="settings.primaryContentHeader"
                                        />
                                        <Toggle
                                            name="settings.primaryContentSeeMore"
                                            label="Primary Content See More"
                                            onText="Link to tag/displays if more than 8 pieces of content"
                                            offText="See More option is off"
                                        />
                                        <AffinityContent 
                                            tagField="settings.primaryContentTagID"
                                            fieldName="primaryContent"
                                            defaultToTagIfEmpty
                                        />
                                    </PivotItem>
                                    <PivotItem headerText="Courses" itemKey="4">
                                        <TextField
                                            label="Courses Header"
                                            name="settings.courseHeader"
                                        />
                                        <Toggle
                                            name="settings.courseSeeMore"
                                            label="Courses See More"
                                            onText="Link to courses page"
                                            offText="See More option is off"
                                        />
                                        <AffinityCourses />
                                    </PivotItem>
                                    <PivotItem headerText="Tools" itemKey="5">
                                        <TextField
                                            label="Tools Header"
                                            name="settings.toolHeader"
                                        />
                                        <Toggle
                                            name="settings.toolSeeMore"
                                            label="Tools See More"
                                            onText="Link to tools page"
                                            offText="See More option is off"
                                        />
                                        <AffinityTools />
                                    </PivotItem>
                                    <PivotItem headerText="Secondary Content" itemKey="6">
                                        <TextField
                                            label="Secondary Content Header"
                                            name="settings.secondaryContentHeader"
                                        />
                                        <Toggle
                                            name="settings.secondaryContentSeeMore"
                                            label="Secondary Content See More"
                                            onText="Link to tag/displays if more than 8 pieces of content"
                                            offText="See More option is off"
                                        />
                                        <AffinityContent
                                            tagField='settings.secondaryContentTagID'
                                            fieldName="secondaryContent"
                                            isSecondary
                                            defaultToTagIfEmpty
                                        />
                                    </PivotItem>
                                    
                                    <PivotItem headerText="Cobrand On Settings" itemKey="7">
                                        <div className="max-800">
                                            <CobrandDropDown
                                                name="enabledCobrands"
                                                label='On for Cobrands'
                                                byNameOrId="id"
                                                setOptionTable=''
                                                multiSelect
                                            />
                                        </div>
                                    </PivotItem>
                                </Pivot>
                            </div>
                        </FormStepWrapper>
                        {summaryObj && (
                            <FeatureSummary
                                label={summaryObj.label}
                                summary={summaryObj.summary}
                                imageUrlPath={summaryObj.imageUrlPath}
                            />
                        )}
                    </>
                    ) : null;
            }}
        </Formik>
    );
};

export default WizardPage;
